import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Container, Grid, Card, Icon, AppBar, Toolbar, TextField, Avatar } from "@mui/material";
import { fadeInUp, fadeInLeft, fadeInRight } from "../../utils/animations";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import PageLayout from "examples/LayoutContainers/PageLayout";
import api from "utils/axiosConfig";

import IconImage from "assets/images/logo.png";

function Contact() {
  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    sujet: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/api/contact/", formData);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: "email",
      title: "Email",
      value: "ehoulogm@yahoo.fr",
      desc: "Réponse sous 24-48h ouvrées",
      href: "mailto:ehoulogm@yahoo.fr",
    },
    {
      icon: "phone",
      title: "Téléphone",
      value: "+229 01 66 36 65 22",
      desc: "Lun - Ven, 8h - 17h (GMT+1)",
      href: "tel:+2290166366522",
    },
    {
      icon: "chat",
      title: "WhatsApp",
      value: "+229 01 66 36 65 22",
      desc: "Discutez avec nous sur WhatsApp",
      href: "https://wa.me/2290166366522",
      isWhatsApp: true,
    },
  ];

  return (
    <PageLayout>
      <Helmet>
        <title>Nous contacter - ConformIMF</title>
        <meta
          name="description"
          content="Contactez l'équipe ConformIMF. Nous répondons à toutes vos questions sur la conformité réglementaire des institutions de microfinance dans l'espace UEMOA."
        />
        <meta property="og:title" content="Nous contacter - ConformIMF" />
        <meta
          property="og:description"
          content="Contactez Guy Modeste EHOULO, expert en audit, conformité et microfinance. Réponse sous 24-48h."
        />
        <meta property="og:url" content="https://conformimf.com/contact" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Nous contacter - ConformIMF" />
        <meta
          name="twitter:description"
          content="Contactez Guy Modeste EHOULO, expert en audit, conformité et microfinance. Réponse sous 24-48h."
        />
      </Helmet>

      {/* Navbar */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(16px)",
          boxShadow: "0 2px 20px rgba(0,0,0,0.08)",
          py: 0.5,
          zIndex: 1100,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: "space-between", px: "0 !important" }}>
            <MDBox
              component={Link}
              to="/"
              display="flex"
              alignItems="center"
              sx={{ textDecoration: "none" }}
            >
              <MDBox component="img" src={IconImage} alt="Logo" width="200px" sx={{ mr: 1.5 }} />
            </MDBox>
            <MDButton
              variant="gradient"
              color="purple"
              size="small"
              component={Link}
              to="/"
              sx={{
                px: 3,
                transition: "transform 0.3s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
            >
              <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
              Accueil
            </MDButton>
          </Toolbar>
        </Container>
      </AppBar>

      <MDBox sx={{ pt: 12, pb: 6, minHeight: "100vh", backgroundColor: "#fafafa" }}>
        {/* ═══════ HERO RESPONSABLE ═══════ */}
        <MDBox
          sx={{
            background: "linear-gradient(135deg, #0A3475 0%, #186A81 100%)",
            py: { xs: 4, md: 6 },
            px: { xs: 2, sm: 0 },
            mb: { xs: 4, md: 6 },
            animation: `${fadeInUp} 0.6s ease-out both`,
          }}
        >
          <Container maxWidth="lg">
            <MDBox
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              alignItems="center"
              gap={{ xs: 2.5, md: 4 }}
            >
              <Avatar
                src="/responsable.png"
                alt="Guy Modeste EHOULO"
                sx={{
                  width: { xs: 100, md: 130 },
                  height: { xs: 100, md: 130 },
                  border: "4px solid rgba(255,255,255,0.4)",
                  boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
                  fontSize: "2.5rem",
                  bgcolor: "rgba(255,255,255,0.15)",
                }}
              >
                GM
              </Avatar>
              <MDBox textAlign={{ xs: "center", md: "left" }}>
                <MDTypography
                  variant="h3"
                  fontWeight="bold"
                  sx={{
                    color: "#fff",
                    mb: 0.5,
                    fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2rem" },
                  }}
                >
                  Guy Modeste EHOULO
                </MDTypography>
                <MDTypography
                  variant="h6"
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    mb: 2,
                    fontSize: { xs: "0.85rem", sm: "1rem" },
                  }}
                >
                  Expert en Audit, Conformité &amp; Microfinance
                </MDTypography>
                <MDBox
                  display="flex"
                  flexWrap="wrap"
                  gap={1.5}
                  justifyContent={{ xs: "center", md: "flex-start" }}
                >
                  <MDButton
                    variant="contained"
                    size="small"
                    component="a"
                    href="mailto:ehoulogm@yahoo.fr"
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.15)",
                      color: "#fff",
                      borderRadius: "10px",
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      px: { xs: 1.5, sm: 2 },
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
                    }}
                  >
                    <Icon sx={{ mr: 0.5, fontSize: { xs: "16px", sm: "20px" } }}>email</Icon>
                    Email
                  </MDButton>
                  <MDButton
                    variant="contained"
                    size="small"
                    component="a"
                    href="tel:+2290166366522"
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.15)",
                      color: "#fff",
                      borderRadius: "10px",
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      px: { xs: 1.5, sm: 2 },
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
                    }}
                  >
                    <Icon sx={{ mr: 0.5, fontSize: { xs: "16px", sm: "20px" } }}>phone</Icon>
                    Appeler
                  </MDButton>
                  <MDButton
                    variant="contained"
                    size="small"
                    component="a"
                    href="https://wa.me/2290166366522"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      backgroundColor: "#25D366",
                      color: "#fff",
                      borderRadius: "10px",
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      px: { xs: 1.5, sm: 2 },
                      "&:hover": { backgroundColor: "#1DA851" },
                    }}
                  >
                    <Icon sx={{ mr: 0.5, fontSize: { xs: "16px", sm: "20px" } }}>chat</Icon>
                    WhatsApp
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          </Container>
        </MDBox>

        <Container maxWidth="lg">
          {/* ═══════ COORDONNÉES (3 CARTES) ═══════ */}
          <Grid container spacing={{ xs: 2, md: 3 }} mb={{ xs: 3, md: 5 }}>
            {contactInfo.map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card
                  component="a"
                  href={item.href}
                  target={item.isWhatsApp ? "_blank" : undefined}
                  rel={item.isWhatsApp ? "noopener noreferrer" : undefined}
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    borderRadius: "16px",
                    textDecoration: "none",
                    display: "block",
                    height: "100%",
                    animation: `${fadeInUp} 0.5s ease-out ${0.1 + i * 0.12}s both`,
                    transition: "transform 0.3s, box-shadow 0.3s",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 30px rgba(123,31,162,0.12)",
                    },
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={2}>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        width: { xs: 46, md: 56 },
                        height: { xs: 46, md: 56 },
                        borderRadius: "14px",
                        background: item.isWhatsApp
                          ? "linear-gradient(135deg, #dcfce7, #bbf7d0)"
                          : "linear-gradient(135deg, #186A81, #0A3475)",
                        flexShrink: 0,
                      }}
                    >
                      <Icon
                        sx={{
                          color: item.isWhatsApp ? "#25D366" : "#FFD740",
                          fontSize: { xs: "22px", md: "26px" },
                        }}
                      >
                        {item.icon}
                      </Icon>
                    </MDBox>
                    <MDBox sx={{ minWidth: 0 }}>
                      <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                        {item.title}
                      </MDTypography>
                      <MDTypography
                        variant="body1"
                        fontWeight="bold"
                        display="block"
                        sx={{
                          color: item.isWhatsApp ? "#25D366" : "#344767",
                          fontSize: { xs: "0.85rem", sm: "1rem" },
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.value}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{ opacity: 0.5, display: { xs: "none", sm: "block" } }}
                      >
                        {item.desc}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* ═══════ FORMULAIRE ═══════ */}
          <Card
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: "20px",
              animation: `${fadeInUp} 0.6s ease-out 0.5s both`,
            }}
          >
            {submitted ? (
              <MDBox textAlign="center" py={6}>
                <Icon sx={{ fontSize: "60px !important", color: "#4CAF50", mb: 2 }}>
                  check_circle
                </Icon>
                <MDTypography variant="h5" fontWeight="bold" mb={1}>
                  Message envoyé !
                </MDTypography>
                <MDTypography variant="body2" color="text" mb={3}>
                  Merci pour votre message. Notre équipe vous répondra dans les meilleurs délais.
                </MDTypography>
                <MDButton
                  variant="gradient"
                  color="purple"
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({ nom: "", email: "", sujet: "", message: "" });
                  }}
                >
                  Envoyer un autre message
                </MDButton>
              </MDBox>
            ) : (
              <>
                <MDBox display="flex" alignItems="center" gap={1.5} mb={0.5}>
                  <Icon sx={{ color: "#0A3475", fontSize: { xs: "24px", md: "28px" } }}>
                    mail_outline
                  </Icon>
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "1.15rem", sm: "1.4rem", md: "1.75rem" } }}
                  >
                    Envoyez-nous un message
                  </MDTypography>
                </MDBox>
                <MDTypography
                  variant="body2"
                  color="text"
                  mb={{ xs: 3, md: 4 }}
                  sx={{ opacity: 0.6, pl: { xs: 0, sm: 5.5 } }}
                >
                  Remplissez le formulaire ci-dessous et nous vous répondrons rapidement.
                </MDTypography>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nom complet *"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        required
                        inputProps={{ maxLength: 255 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email *"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Sujet *"
                        name="sujet"
                        value={formData.sujet}
                        onChange={handleChange}
                        required
                        inputProps={{ maxLength: 500 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Votre message *"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        multiline
                        rows={6}
                        required
                      />
                    </Grid>
                  </Grid>
                  <MDBox
                    mt={3}
                    display="flex"
                    flexDirection="column"
                    alignItems={{ xs: "center", sm: "flex-end" }}
                    gap={1.5}
                  >
                    {error && (
                      <MDTypography
                        variant="body2"
                        sx={{
                          color: "#d32f2f",
                          fontWeight: "medium",
                          textAlign: "center",
                          width: "100%",
                        }}
                      >
                        {error}
                      </MDTypography>
                    )}
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="purple"
                      size="large"
                      disabled={loading}
                      sx={{
                        px: { xs: 3, sm: 5 },
                        py: 1.5,
                        borderRadius: "12px",
                        width: { xs: "100%", sm: "auto" },
                        transition: "transform 0.3s, box-shadow 0.3s",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 25px rgba(123,31,162,0.3)",
                        },
                      }}
                    >
                      <Icon sx={{ mr: 1 }}>{loading ? "hourglass_empty" : "send"}</Icon>
                      {loading ? "Envoi en cours..." : "Envoyer le message"}
                    </MDButton>
                  </MDBox>
                </form>
              </>
            )}
          </Card>
        </Container>

        {/* Footer */}
        <MDBox textAlign="center" mt={8}>
          <MDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>
            &copy; 2026 ConformIMF - Guy Modeste EHOULO - Tous droits réservés
          </MDTypography>
        </MDBox>
      </MDBox>
    </PageLayout>
  );
}

export default Contact;
